import { useOnChange } from "@prismicio/editor-support/React"
import {
  Checkbox,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  FormInput,
  InlineLabel,
} from "@prismicio/editor-ui"
import { useState } from "react"

export interface WebLinkOptions {
  newTab: boolean
}

interface WebLinkModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  url: string
  openInNewTab: boolean
  showNewTabButton: boolean
  onUpdate: (url: string, options: WebLinkOptions) => void
}

export function WebLinkModal(props: WebLinkModalProps) {
  const { open, onOpenChange: setOpen, showNewTabButton } = props

  const { url, setUrl, openInNewTab, setOpenInNewTab, disabled, onUpdate } =
    useWebLinkModal(props)

  return (
    <Dialog open={open} onOpenChange={setOpen} size="small" noDescription>
      <DialogHeader title="Set Link URL" />

      <DialogContent padding={16}>
        <FormInput
          value={url}
          onValueChange={setUrl}
          placeholder="Paste URL"
          icon="public"
        />
        {showNewTabButton && (
          <InlineLabel value="Open in a new tab">
            <Checkbox
              checked={openInNewTab}
              onCheckedChange={setOpenInNewTab}
            />
          </InlineLabel>
        )}
      </DialogContent>

      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton onClick={onUpdate} disabled={disabled}>
          Update
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function useWebLinkModal(props: WebLinkModalProps) {
  const {
    url: initialUrl,
    openInNewTab: initialTab,
    onUpdate: onUpdateAction,
    open,
  } = props

  const [url, setUrl] = useState(initialUrl)
  const [openInNewTab, setOpenInNewTab] = useState(initialTab)

  useOnChange(open, () => {
    if (!open) return

    setUrl(initialUrl)
    setOpenInNewTab(initialTab)
  })

  const disabled = !url || (url === initialUrl && openInNewTab === initialTab)

  function onUpdate() {
    onUpdateAction(url, { newTab: openInNewTab })
  }

  return {
    url,
    setUrl,
    openInNewTab,
    disabled,
    setOpenInNewTab,
    onUpdate,
  }
}
